/**
 * SecurityManagement - Gate Pass and Security Scanning
 * For youth/kids conferences with leave permissions and venue scanning
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Shield, LogIn, LogOut, QrCode, Clock, CheckCircle, XCircle,
  AlertTriangle, Users, FileText, Search, Loader2, ArrowLeftRight,
  Utensils, Home, Calendar, UserCheck, UserX
} from 'lucide-react';
import api from '@/lib/api';

const SCAN_TYPES = {
  gate_in: { label: 'Gate In', icon: LogIn, color: 'bg-green-100 text-green-800' },
  gate_out: { label: 'Gate Out', icon: LogOut, color: 'bg-red-100 text-red-800' },
  meal: { label: 'Meal', icon: Utensils, color: 'bg-orange-100 text-orange-800' },
  dorm: { label: 'Dorm/Housing', icon: Home, color: 'bg-blue-100 text-blue-800' },
  session: { label: 'Session', icon: Calendar, color: 'bg-purple-100 text-purple-800' }
};

const PASS_STATUSES = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800' },
  returned: { label: 'Returned', color: 'bg-blue-100 text-blue-800' }
};

const SecurityManagement = ({ eventId }) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('scanner');
  const [scanForm, setScanForm] = useState({ delegate_id: '', scan_type: 'gate_in', location: '', notes: '' });
  const [passForm, setPassForm] = useState({ delegate_id: '', reason: '', expected_return: '', guardian_name: '', guardian_contact: '', notes: '' });
  const [showPassDialog, setShowPassDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch security stats
  const { data: stats = {} } = useQuery({
    queryKey: ['security-stats', eventId],
    queryFn: () => api.get(`/admin/events/${eventId}/security/stats`).then(r => r.data),
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  // Fetch gate passes
  const { data: gatePasses = [], isLoading: passesLoading } = useQuery({
    queryKey: ['gate-passes', eventId],
    queryFn: () => api.get(`/admin/events/${eventId}/gate-passes`).then(r => r.data)
  });

  // Fetch recent scans
  const { data: recentScans = [] } = useQuery({
    queryKey: ['security-scans', eventId],
    queryFn: () => api.get(`/admin/events/${eventId}/security/scans?limit=50`).then(r => r.data)
  });

  // Fetch delegates for search
  const { data: delegates = [] } = useQuery({
    queryKey: ['delegates', eventId],
    queryFn: () => api.get(`/admin/events/${eventId}/delegates`).then(r => r.data)
  });

  // Security scan mutation
  const scanMutation = useMutation({
    mutationFn: (data) => api.post(`/admin/events/${eventId}/security/scan`, data),
    onSuccess: (res) => {
      const scanType = SCAN_TYPES[res.data.scan_type];
      toast.success(`${scanType?.label || 'Scan'} recorded for ${res.data.delegate_name}`);
      queryClient.invalidateQueries(['security-scans', eventId]);
      queryClient.invalidateQueries(['security-stats', eventId]);
      setScanForm({ ...scanForm, delegate_id: '', notes: '' });
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Scan failed')
  });

  // Create gate pass mutation
  const createPassMutation = useMutation({
    mutationFn: (data) => api.post(`/admin/events/${eventId}/gate-passes`, data),
    onSuccess: () => {
      toast.success('Gate pass request created');
      queryClient.invalidateQueries(['gate-passes', eventId]);
      setShowPassDialog(false);
      setPassForm({ delegate_id: '', reason: '', expected_return: '', guardian_name: '', guardian_contact: '', notes: '' });
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to create gate pass')
  });

  // Approve/reject gate pass mutation
  const approvePassMutation = useMutation({
    mutationFn: ({ passId, approved, notes }) => 
      api.put(`/admin/events/${eventId}/gate-passes/${passId}/approve`, { approved, notes }),
    onSuccess: (_, { approved }) => {
      toast.success(`Gate pass ${approved ? 'approved' : 'rejected'}`);
      queryClient.invalidateQueries(['gate-passes', eventId]);
      queryClient.invalidateQueries(['security-stats', eventId]);
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Action failed')
  });

  // Mark returned mutation
  const returnMutation = useMutation({
    mutationFn: (passId) => api.put(`/admin/events/${eventId}/gate-passes/${passId}/return`),
    onSuccess: () => {
      toast.success('Delegate marked as returned');
      queryClient.invalidateQueries(['gate-passes', eventId]);
      queryClient.invalidateQueries(['security-stats', eventId]);
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Action failed')
  });

  const handleScan = () => {
    if (!scanForm.delegate_id) {
      toast.error('Please select or enter a delegate');
      return;
    }
    scanMutation.mutate(scanForm);
  };

  const filteredDelegates = delegates.filter(d => 
    d.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-2xl font-bold">{stats.total_delegates || 0}</p>
                <p className="text-xs text-muted-foreground">Total Delegates</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-green-600" />
              <div>
                <p className="text-2xl font-bold">{stats.on_premises || 0}</p>
                <p className="text-xs text-muted-foreground">On Premises</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <UserX className="h-5 w-5 text-red-600" />
              <div>
                <p className="text-2xl font-bold">{stats.off_premises || 0}</p>
                <p className="text-xs text-muted-foreground">Off Premises</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-yellow-600" />
              <div>
                <p className="text-2xl font-bold">{stats.pending_gate_passes || 0}</p>
                <p className="text-xs text-muted-foreground">Pending Passes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <ArrowLeftRight className="h-5 w-5 text-orange-600" />
              <div>
                <p className="text-2xl font-bold">{stats.currently_out_with_pass || 0}</p>
                <p className="text-xs text-muted-foreground">Out with Pass</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3">
          <TabsTrigger value="scanner" className="flex items-center gap-2">
            <QrCode className="h-4 w-4" />
            Scanner
          </TabsTrigger>
          <TabsTrigger value="gate-passes" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Gate Passes
            {stats.pending_gate_passes > 0 && (
              <Badge variant="destructive" className="ml-1">{stats.pending_gate_passes}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Scan History
          </TabsTrigger>
        </TabsList>

        {/* Scanner Tab */}
        <TabsContent value="scanner" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Security Scanner
              </CardTitle>
              <CardDescription>
                Scan delegates for gate entry/exit, meals, dorm access, or session attendance
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Delegate</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input 
                      placeholder="Search delegate..." 
                      className="pl-9"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  {searchQuery && filteredDelegates.length > 0 && (
                    <div className="border rounded-md max-h-48 overflow-auto">
                      {filteredDelegates.slice(0, 10).map(d => (
                        <div 
                          key={d.id}
                          className="p-2 hover:bg-muted cursor-pointer flex justify-between items-center"
                          onClick={() => {
                            setScanForm({ ...scanForm, delegate_id: d.id });
                            setSearchQuery(d.name);
                          }}
                        >
                          <div>
                            <p className="font-medium">{d.name}</p>
                            <p className="text-xs text-muted-foreground">{d.email}</p>
                          </div>
                          {d.is_on_premises !== undefined && (
                            <Badge variant={d.is_on_premises ? 'default' : 'secondary'}>
                              {d.is_on_premises ? 'On Site' : 'Off Site'}
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Scan Type</Label>
                  <Select 
                    value={scanForm.scan_type} 
                    onValueChange={(v) => setScanForm({ ...scanForm, scan_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(SCAN_TYPES).map(([key, { label, icon: Icon }]) => (
                        <SelectItem key={key} value={key}>
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4" />
                            {label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Location (Optional)</Label>
                  <Input 
                    placeholder="e.g., Main Gate, Cafeteria, Dorm A"
                    value={scanForm.location}
                    onChange={(e) => setScanForm({ ...scanForm, location: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Notes (Optional)</Label>
                  <Input 
                    placeholder="Any additional notes"
                    value={scanForm.notes}
                    onChange={(e) => setScanForm({ ...scanForm, notes: e.target.value })}
                  />
                </div>
              </div>

              <Button 
                className="w-full" 
                size="lg"
                onClick={handleScan}
                disabled={scanMutation.isPending || !scanForm.delegate_id}
              >
                {scanMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <QrCode className="h-4 w-4 mr-2" />
                )}
                Record Scan
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Gate Passes Tab */}
        <TabsContent value="gate-passes" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Gate Pass Requests</CardTitle>
                <CardDescription>Manage leave permissions for delegates</CardDescription>
              </div>
              <Button onClick={() => setShowPassDialog(true)}>
                <FileText className="h-4 w-4 mr-2" />
                New Gate Pass
              </Button>
            </CardHeader>
            <CardContent>
              {passesLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : gatePasses.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No gate pass requests</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Delegate</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Guardian</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {gatePasses.map(pass => (
                      <TableRow key={pass.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{pass.delegate_name}</p>
                            <p className="text-xs text-muted-foreground">{pass.delegate_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>{pass.reason}</TableCell>
                        <TableCell>
                          {pass.guardian_name ? (
                            <div>
                              <p>{pass.guardian_name}</p>
                              <p className="text-xs text-muted-foreground">{pass.guardian_contact}</p>
                            </div>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge className={PASS_STATUSES[pass.status]?.color}>
                            {PASS_STATUSES[pass.status]?.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            {pass.status === 'pending' && (
                              <>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-green-600"
                                  onClick={() => approvePassMutation.mutate({ passId: pass.id, approved: true })}
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-red-600"
                                  onClick={() => approvePassMutation.mutate({ passId: pass.id, approved: false })}
                                >
                                  <XCircle className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                            {pass.status === 'approved' && (
                              <Button 
                                size="sm"
                                onClick={() => returnMutation.mutate(pass.id)}
                              >
                                Mark Returned
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Scan History Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Recent Scans</CardTitle>
              <CardDescription>Last 50 security scan records</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>Delegate</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Location</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentScans.map(scan => {
                    const scanType = SCAN_TYPES[scan.scan_type];
                    return (
                      <TableRow key={scan.id}>
                        <TableCell className="text-sm">
                          {new Date(scan.scanned_at).toLocaleString()}
                        </TableCell>
                        <TableCell>{scan.delegate_name}</TableCell>
                        <TableCell>
                          <Badge className={scanType?.color}>
                            {scanType?.label || scan.scan_type}
                          </Badge>
                        </TableCell>
                        <TableCell>{scan.location || '-'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* New Gate Pass Dialog */}
      <Dialog open={showPassDialog} onOpenChange={setShowPassDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Request Gate Pass</DialogTitle>
            <DialogDescription>
              Create a leave permission request for a delegate
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Delegate</Label>
              <Select 
                value={passForm.delegate_id} 
                onValueChange={(v) => setPassForm({ ...passForm, delegate_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select delegate" />
                </SelectTrigger>
                <SelectContent>
                  {delegates.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.name} ({d.email})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Reason for Leave</Label>
              <Textarea 
                placeholder="e.g., Medical appointment, Family emergency"
                value={passForm.reason}
                onChange={(e) => setPassForm({ ...passForm, reason: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Guardian Name (for minors)</Label>
                <Input 
                  placeholder="Parent/Guardian name"
                  value={passForm.guardian_name}
                  onChange={(e) => setPassForm({ ...passForm, guardian_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Guardian Contact</Label>
                <Input 
                  placeholder="Phone number"
                  value={passForm.guardian_contact}
                  onChange={(e) => setPassForm({ ...passForm, guardian_contact: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Expected Return Time</Label>
              <Input 
                type="datetime-local"
                value={passForm.expected_return}
                onChange={(e) => setPassForm({ ...passForm, expected_return: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPassDialog(false)}>Cancel</Button>
            <Button 
              onClick={() => createPassMutation.mutate(passForm)}
              disabled={createPassMutation.isPending || !passForm.delegate_id || !passForm.reason}
            >
              {createPassMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SecurityManagement;
